/**
 * @tt/motion — single source of truth for motion / feel constants.
 *
 * Both Pixi canvases (per-game polish primitives in @tt/polish) and
 * React chrome (`web/src/styles/global.css` already mirrors most of
 * these as CSS custom properties) read from this module.
 *
 * Mirrors the motion language defined in `UI-Gameplan.md` §1.5.
 */

export const TOAST = {
  inMs: 320,
  holdMs: 2400,
  outMs: 220,
} as const;

export const SPRINGS = {
  /** Predictable UI motion. Cubic bezier "ease-out" curve. */
  ui: 'cubic-bezier(0.2, 0, 0, 1)',
  /** Game-feel motion — overshoot then settle. */
  game: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/** Three shake intensities. Each runs the listed duration with
 *  decaying-sine displacement. The user's `data-shake` (0/1/2/3)
 *  multiplier is applied externally — 0 = off, 1/2/3 scale amplitude.
 */
export const SHAKE = {
  tier1: { amplitude: 4,  duration: 180 },
  tier2: { amplitude: 8,  duration: 240 },
  tier3: { amplitude: 14, duration: 340 },
} as const;

/** Hit-stop pauses the per-game tick for these durations on heavy
 *  contact (KOs, weapon connects, log hits). */
export const HIT_STOP = {
  light:  80,
  medium: 110,
  heavy:  140,
} as const;

/** Slow-mo scales tick dt for a window. */
export const SLOW_MO = {
  durationMs: 900,
  timeScale: 0.35,
} as const;

/** Camera pop on big events. */
export const CAMERA_POP = {
  durationMs: 260,
  scaleFrom: 1.0,
  scalePeak: 1.06,
} as const;

/** Read the user's reduce-shake setting from <html data-shake>.
 *  Returns a 0..1 multiplier (0 = off, 0.33 = low, 0.66 = medium, 1 = full).
 *  Safe to call from server-side / non-browser contexts (returns 1). */
export function readShakeMultiplier(): number {
  if (typeof document === 'undefined') return 1;
  const v = document.documentElement.dataset['shake'];
  if (v === '0') return 0;
  if (v === '1') return 0.33;
  if (v === '2') return 0.66;
  return 1;
}

/** Read the user's reduce-motion preference. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}
